import { Loader } from "@common/handleErrorAndLoading/Loader";
import UploadFileButton from "@common/uploadFileButton/UploadFileButton";
import { Button, Grid, MenuItem, Select, Typography } from "@material-ui/core";
import { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useUploadIdentityDocument } from "./hooks/useUploadIdentityDocument";

interface IParams {
    handleCancelClick(): void;
    onFinishUpdate(): void;
}

interface DocumentType {
    value: string;
    label: string;
}

const documentTypes: DocumentType[] = [
    { value: "ID", label: "idDocument" },
    { value: "PASSPORT", label: "passport" },
];

const UpdateUserIdentity = (props: IParams) => {
    const { handleCancelClick, onFinishUpdate } = props;
    const { formMethods, handleOnSubmit, uploadIdentityDocumentState } = useUploadIdentityDocument(onFinishUpdate);
    const { t } = useTranslation(["profile", "glossary", "common", "validation"]);
    const { errors, control, setValue } = formMethods;
    const [idDocumentType, setIdDocumentType] = useState<string>(documentTypes[0].value);

    useEffect(() => {
        setValue("type", "ID");
    }, []);

    return (
        <Grid container item>
            <form className="mydetail-email-form" onSubmit={handleOnSubmit}>
                <Controller
                    control={control}
                    name={"type"}
                    rules={{
                        required: t("validation:required"),
                    }}
                    render={(renderProps) => (
                        <Grid container direction="column" spacing={1}>
                            <Grid item xs={12}>
                                <Typography variant="body1">{t("profile:selectDocType")}</Typography>
                            </Grid>
                            <Grid item xs={10} sm={4} md={3}>
                                <Select
                                    inputRef={renderProps.ref}
                                    disableUnderline
                                    displayEmpty
                                    fullWidth
                                    id="doc-type"
                                    name={renderProps.name}
                                    variant="filled"
                                    value={renderProps.value || idDocumentType}
                                    onChange={(event) => {
                                        setIdDocumentType(event.target.value as string);
                                        setValue("frontBase64", undefined);
                                        setValue("backBase64", undefined);
                                        return renderProps.onChange(event);
                                    }}
                                >
                                    {documentTypes.map((docType) => (
                                        <MenuItem key={docType.value} value={docType.value}>
                                            {t(`profile:${docType.label}` as any)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>
                    )}
                />
                {idDocumentType === "ID" && (
                    <>
                        <Grid className="mydetail-email__info" item sm={8} xs={12}>
                            <Typography variant="body1">{t("profile:updateUserIdentityHelper")}</Typography>
                        </Grid>
                        <Grid item container direction="column" justify="center" alignItems="center" spacing={1}>
                            <Grid item>
                                <UploadFileButton
                                    error={errors.frontBase64}
                                    control={control}
                                    name={"frontBase64"}
                                    buttonText={`${t("profile:uploadFront")} *`}
                                />
                            </Grid>
                            <Grid item>
                                <UploadFileButton
                                    error={errors.backBase64}
                                    control={control}
                                    name={"backBase64"}
                                    buttonText={`${t("profile:uploadBack")} *`}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
                {idDocumentType === "PASSPORT" && (
                    <>
                        <Grid className="mydetail-email__info" item sm={8} xs={12}>
                            <Typography variant="body1">{t("profile:updatePassportHelper")}</Typography>
                        </Grid>
                        <Grid item container direction="column" justify="center" alignItems="center" spacing={1}>
                            <Grid item>
                                <UploadFileButton
                                    error={errors.frontBase64}
                                    control={control}
                                    name={"frontBase64"}
                                    buttonText={`${t("profile:uploadPassport")} *`}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {uploadIdentityDocumentState.error && (
                    <Grid className="mydetail-email__info" item sm={8} xs={12}>
                        <Typography color="error" variant="body1">
                            {uploadIdentityDocumentState.error.errorMessage}
                        </Typography>
                    </Grid>
                )}
                <Grid container item justify="center" alignItems="center" style={{ margin: "10px 0 10px 0" }}>
                    <Grid
                        item
                        container
                        spacing={2}
                        direction="row"
                        sm={8}
                        xs={12}
                        justify="center"
                        alignItems="center"
                    >
                        {uploadIdentityDocumentState.isLoading && <Loader size={40} />}
                        {!uploadIdentityDocumentState.isLoading && (
                            <>
                                <Grid item xs={4}>
                                    <Button color="secondary" fullWidth variant="outlined" onClick={handleCancelClick}>
                                        {t("common:cancel")}
                                    </Button>
                                </Grid>
                                <Grid item xs={4}>
                                    <Button
                                        color="secondary"
                                        formNoValidate
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                    >
                                        {t("glossary:save")}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
};

export default UpdateUserIdentity;
